import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * `mail_signals/{userEmailLower}` = { mailboxes: { [mailboxKey]: { version, inboxUnread, updatedAt } } }.
 * One live subscription per email shared by every consumer (sidebar badge +
 * Email page). The backend bumps `version` whenever a mailbox changes.
 */

const EMPTY = Object.freeze({ mailboxes: {} });

// email → { value, loaded, unsubscribe, listeners:Set }
const subscriptions = new Map();

function normalise(data) {
  const mailboxes = data && typeof data.mailboxes === 'object' && data.mailboxes ? data.mailboxes : {};
  return { mailboxes };
}

function ensure(email) {
  let entry = subscriptions.get(email);
  if (entry) return entry;
  entry = { value: EMPTY, loaded: false, unsubscribe: null, listeners: new Set() };
  subscriptions.set(email, entry);
  entry.unsubscribe = onSnapshot(
    doc(db, 'mail_signals', email),
    (snap) => {
      entry.value = snap.exists() ? normalise(snap.data()) : EMPTY;
      entry.loaded = true;
      entry.listeners.forEach((fn) => fn(entry.value));
    },
    () => {
      // Permission / offline: keep whatever we had; consumers see an empty map.
      entry.loaded = true;
      entry.listeners.forEach((fn) => fn(entry.value));
    },
  );
  return entry;
}

function release(email) {
  const entry = subscriptions.get(email);
  if (!entry || entry.listeners.size > 0) return;
  if (entry.unsubscribe) entry.unsubscribe();
  subscriptions.delete(email);
}

/** @returns {{ mailboxes: Record<string, { version?: number, inboxUnread?: number, updatedAt?: any }> }} */
export function useMailSignals(userEmail) {
  const email = String(userEmail || '').trim().toLowerCase();
  const [value, setValue] = useState(() => (email && subscriptions.get(email)?.value) || EMPTY);

  useEffect(() => {
    if (!email) {
      setValue(EMPTY);
      return undefined;
    }
    const entry = ensure(email);
    const listener = (next) => setValue(next);
    entry.listeners.add(listener);
    if (entry.loaded) setValue(entry.value);
    return () => {
      entry.listeners.delete(listener);
      release(email);
    };
  }, [email]);

  return value;
}

export default useMailSignals;
